import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowDown, ArrowUp, Pencil, X } from "@phosphor-icons/react";
import InventoryItemCombobox from "@/components/InventoryItemCombobox";

export default function Stock() {
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogType, setDialogType] = useState(null); // "in" | "out" | "adjust"

  const load = async () => {
    setLoading(true);
    try {
      const [mv, it] = await Promise.all([
        api.get("/inventory/movements", { params: { limit: 200 } }),
        api.get("/inventory/items"),
      ]);
      setMovements(mv.data || []);
      setItems(it.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fmtDate = (s) => new Date(s).toLocaleString();

  return (
    <div className="space-y-5" data-testid="inv-stock-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
            Inventory · Movements
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Stock Ledger</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every IN, OUT, and adjustment — FIFO/FEFO-aware, batch-tracked.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDialogType("in")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="stock-in-btn"
          >
            <ArrowDown size={14} weight="bold" /> Stock In
          </button>
          <button
            onClick={() => setDialogType("out")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700"
            data-testid="stock-out-btn"
          >
            <ArrowUp size={14} weight="bold" /> Stock Out
          </button>
          <button
            onClick={() => setDialogType("adjust")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50"
            data-testid="stock-adjust-btn"
          >
            <Pencil size={14} weight="bold" /> Adjust
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["When", "Type", "Item", "Qty", "Batch", "Vendor / Ref", "By"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && movements.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-sm text-slate-500">No movements yet. Start with Stock In.</td></tr>
            )}
            {movements.map((m) => (
              <tr key={m.id} className="border-t border-slate-100" data-testid={`movement-row-${m.id}`}>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(m.timestamp)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${typeTone(m.type)}`}>{m.type}</span>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{m.item_name}</td>
                <td className="px-4 py-3 text-sm tabular-nums">{m.qty} {m.uom}</td>
                <td className="px-4 py-3 text-xs text-slate-500 font-mono">{m.batch_no || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{m.vendor_name || m.reference || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{m.created_by_name || m.created_by_email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialogType && (
        <MovementDialog
          type={dialogType}
          items={items}
          onClose={() => setDialogType(null)}
          onDone={() => { setDialogType(null); load(); }}
        />
      )}
    </div>
  );
}

function typeTone(t) {
  if (t === "in") return "bg-emerald-50 text-emerald-700";
  if (t === "out") return "bg-rose-50 text-rose-700";
  if (t === "adjust") return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

function MovementDialog({ type, items, onClose, onDone }) {
  const [form, setForm] = useState({
    item_id: "", qty: "", batch_no: "", lot_no: "", expiry_date: "",
    unit_cost: "", vendor_name: "", grn_no: "", to_location: "MAIN",
    from_location: "", reference: "", note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        ...form,
        type,
        qty: Number(form.qty || 0),
        unit_cost: Number(form.unit_cost || 0),
      };
      await api.post("/inventory/stock/movement", body);
      toast.success(`Stock ${type.toUpperCase()} recorded`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const title = type === "in" ? "Stock In (GRN)" : type === "out" ? "Stock Out / Issue" : "Stock Adjustment";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid={`movement-dialog-${type}`}>
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="p-2 text-slate-500 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Item *">
            <InventoryItemCombobox
              items={items}
              value={form.item_id}
              onPick={(v) => set("item_id", v)}
              testId="mv-item-select"
              placeholder="Select item…"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity *">
              <input required type="number" step="0.001" value={form.qty} onChange={(e) => set("qty", e.target.value)} className={inp} data-testid="mv-qty" />
            </Field>
            <Field label="Unit Cost (₹)">
              <input type="number" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} className={inp} data-testid="mv-unit-cost" />
            </Field>
          </div>
          {type !== "out" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={type === "adjust" ? "Batch No * (existing)" : "Batch No"}>
                <input required={type === "adjust"} value={form.batch_no} onChange={(e) => set("batch_no", e.target.value)} className={inp} data-testid="mv-batch-no" />
              </Field>
              <Field label="Expiry Date">
                <input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className={inp} data-testid="mv-expiry" />
              </Field>
            </div>
          )}
          {type === "in" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vendor">
                  <input value={form.vendor_name} onChange={(e) => set("vendor_name", e.target.value)} className={inp} data-testid="mv-vendor" />
                </Field>
                <Field label="GRN No">
                  <input value={form.grn_no} onChange={(e) => set("grn_no", e.target.value)} className={inp} data-testid="mv-grn" />
                </Field>
              </div>
              <Field label="Storage Location">
                <input value={form.to_location} onChange={(e) => set("to_location", e.target.value)} className={inp} />
              </Field>
            </>
          )}
          <Field label="Reference / Note">
            <input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="PO #, batch ID, recipe ID…" className={inp} data-testid="mv-ref" />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button
            type="submit"
            disabled={submitting}
            className={`h-10 px-5 rounded-md text-white text-sm font-medium ${type === "in" ? "bg-emerald-600 hover:bg-emerald-700" : type === "out" ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"} disabled:opacity-50`}
            data-testid="mv-submit"
          >
            {submitting ? "Saving…" : `Record ${type.toUpperCase()}`}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
